import {
  DESTINATION_DELPHI_FLAG,
  DESTINATION_HERMES_FLAG,
  DESTINATION_MEMBER_FLAG,
  DESTINATION_ONBOARDING_FLAG,
  getSupportEmail,
  getSupportUrl,
  getVidaWebUrl,
  getZendeskBaseUrl,
  type LoginFeatureFlagKey,
} from '../config/runtimeConfig';
import { loginTx } from '../i18n/loginTx';

const t = (str: string) => loginTx.translate(str);

export const ROUTES = {
  LOGIN: './',
  SSO: './sso-login',
  TWO_FACTOR: './two-factor',
  FORGOT_PASSWORD: './forgot-password',
  RESET_PASSWORD: './reset-password',
  MAGIC_LINK: './magic-link',
  MAGIC_LINK_SENT: './magic-link-sent',
  TOKEN_LOGIN: './token-login',
  MAINTENANCE: './maintenance',
} as const;

// Paths for each `next_app` identifier the API can return, on both the
// legacy webserver domain (VIDA_WEB_URL) and the GCS SPA-router domain
// (same origin as login: staging-app.vida.com / app.vida.com). These are
// the same paths — every app is served under the same URL segment
// regardless of which host is currently in front.
//
// `satisfies` narrows dot-access to `string` (not `string | undefined`)
// so composed constants below type cleanly under noUncheckedIndexedAccess.
const APP_PATHS = {
  member_app:        '/app/',
  member_legacy_app: '/clients/',
  member_onboarding: '/clients/onboarding/',
  provider_app:      '/coaching/',
  care_coordinator:  '/auditing/task-management',
  health_guide:      '/web/admin',
} satisfies Record<string, string>;

// Proxy so reads compose against the CURRENT vidaWebUrl, not module-load.
export const APP_DESTINATIONS: Record<string, string> = new Proxy(
  {} as Record<string, string>,
  {
    get(_, key) {
      if (typeof key !== 'string') return undefined;
      const path = APP_PATHS[key as keyof typeof APP_PATHS];
      return path === undefined ? undefined : `${getVidaWebUrl()}${path}`;
    },
    has(_, key) {
      return typeof key === 'string' && key in APP_PATHS;
    },
    ownKeys() {
      return Object.keys(APP_PATHS);
    },
    getOwnPropertyDescriptor(_, key) {
      if (typeof key !== 'string' || !(key in APP_PATHS)) return undefined;
      const path = APP_PATHS[key as keyof typeof APP_PATHS];
      return {
        enumerable: true,
        configurable: true,
        writable: false,
        value: `${getVidaWebUrl()}${path}`,
      };
    },
  }
);

// Default post-login destination — fallback when next_app is unrecognised
// or a flag lookup fails. Function so it reads the current vidaWebUrl.
export const getDefaultMemberAppUrl = (): string =>
  `${getVidaWebUrl()}${APP_PATHS.member_app}`;

// Same paths as APP_PATHS but re-exported under the historical name.
// Consumers reach for this when they need the GCS-relative form (path only,
// no host) — e.g. when PostHog flag routes the user through the spa-router
// on the same origin instead of a full navigation to the webserver host.
export const GCS_APP_DESTINATIONS: Record<string, string> = APP_PATHS;

// Maps each next_app key to its PostHog feature flag (one flag per user-facing app).
export const DESTINATION_FLAGS = {
  member_app:        DESTINATION_MEMBER_FLAG,
  member_legacy_app: DESTINATION_MEMBER_FLAG,
  member_onboarding: DESTINATION_ONBOARDING_FLAG,
  provider_app:      DESTINATION_DELPHI_FLAG,
  care_coordinator:  DESTINATION_DELPHI_FLAG,
  health_guide:      DESTINATION_HERMES_FLAG,
} as const satisfies Record<string, LoginFeatureFlagKey>;

// Onboarding 2.0 (React Native app, built by Brian) — GCS-only, served by the
// spa-router at /onboarding/ on staging-app.vida.com / app.vida.com.
// When ONBOARDING_RN_FLAG resolves to ONBOARDING_RN_VARIANT,
// next_app=member_onboarding routes here instead of the sparta-served
// /clients/onboarding/ path.
export const ONBOARDING_RN_PATH = '/onboarding/';

// Getters so every read composes against the current runtime config.
export const EXTERNAL_URLS = {
  get POST_LOGIN() {
    return `${getVidaWebUrl()}${APP_PATHS.member_legacy_app}`;
  },
  get ONBOARDING() {
    return `${getVidaWebUrl()}${APP_PATHS.member_onboarding}`;
  },
  get SUPPORT() {
    return getSupportUrl();
  },
  get SUPPORT_EMAIL() {
    return `mailto:${getSupportEmail()}`;
  },
  // Base URL of the Vida-owned Zendesk account. /access/jwt is the Web SSO
  // entry point — we redirect here with a signed JWT to establish a Zendesk
  // session for support.vida.com.
  get ZENDESK() {
    return getZendeskBaseUrl();
  },
};


export const ERROR_MESSAGES = {
  get INVALID_CREDENTIALS() { return t('Please enter a correct email and password. Note that both fields may be case-sensitive.'); },
  get INVALID_2FA_CODE() { return t('The code you have entered failed. Please try again.'); },
  get SSO_NOT_SUPPORTED() { return t("Sorry, SSO isn't yet supported for your organization. Click 'Back' to login or 'Sign up now' using the link below."); },
  get ENTER_ORG_NAME() { return t("Please, enter your organization's name."); },
  get CHOOSE_ORG_FROM_LIST() { return t('Please, choose your organization from the list.'); },
  get PASSWORD_RESET_SUCCESS() { return t('Successfully sent password reset email'); },
  get PASSWORD_RESET_FAILED() { return t('Failed to send password reset email'); },
  get EMAIL_LOGIN_LINK_SUCCESS() { return t('Sign in link has been sent to your email'); },
  get EMAIL_LOGIN_LINK_FAILED() { return t('Failed to send sign in link. Please try again'); },
  get EMAIL_LOGIN_LINK_RATE_LIMITED() { return t('Too many requests. Please wait a few minutes before trying again.'); },
};
